import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { FEATURE_VULNS, PERMISSION_TYPES } from 'Hooks/usePermission/permissions';
import { selectAllowedByRole } from 'Hooks/usePermission/selector';
import { useIntl } from 'react-intl';
import { selectIsReadOnly } from 'store/Faraday/selectors';
import Button from 'shared/ui/Button';
import { Trash } from '../../styled';
import ModalWarning from 'Common/Components/ModalWarning';
import {
  ModalTitle, Title, BodyText, ModalContent, ButtonsContainer,
  ConfirmButton, CancelButton, Warning, ModalBody
} from '../DeleteConfirmationModal/styled';
import ModalWrapper from 'Common/Components/ModalWrapper';

const ExploreDeleteConfirmModal = ({ count, entity, onConfirm, onCancel }) => {
  const countEntity = count === 1 ? entity : `${entity}s`;
  return (
    <ModalWrapper>
      <ModalContent>
        <ModalTitle>
          <Warning />
          <Title>{ `Delete ${countEntity}` }</Title>
        </ModalTitle>
        <ModalBody>
          <BodyText>{ `You're about to delete ${count} ${countEntity}. This action cannot be undone. Are you sure you want to proceed?` }</BodyText>
        </ModalBody>
        <ButtonsContainer>
          <CancelButton onClick={ onCancel }>Cancel</CancelButton>
          <ConfirmButton onClick={ onConfirm }>Delete</ConfirmButton>
        </ButtonsContainer>
      </ModalContent>
    </ModalWrapper>
  );
};

const DeleteButton = ({
  hasVulns,
  vulnsSelected,
  showVulnDetail,
  showModalDelete,
  entity,
  isExploreAssetsTab,
  isExploreServicesTab,
  selectedHostIds,
  selectedServiceIds,
  deleteExploreHosts,
  deleteExploreServices
}) => {
  const [showWarning, setShowWarning] = useState(false);
  const [showExploreConfirm, setShowExploreConfirm] = useState(false);
  const canDelete = useSelector((state) => selectAllowedByRole(state, FEATURE_VULNS, PERMISSION_TYPES.DELETE));
  const readOnly = useSelector(selectIsReadOnly);
  const intl = useIntl();
  const vulnsWarning = intl.formatMessage({ id: 'modalWarning.selectOneOrMore' }, { entity: 'vulnerabilities' });

  const isExploreTab = isExploreAssetsTab || isExploreServicesTab;
  const exploreEntity = isExploreAssetsTab ? 'Asset' : 'Service';
  const exploreSelected = isExploreAssetsTab ? (selectedHostIds ?? []) : (selectedServiceIds ?? []);

  let canUpdate;
  if (isExploreTab) canUpdate = !readOnly && canDelete;
  else if (entity === 'vulnsGeneral') canUpdate = !showVulnDetail && canDelete && hasVulns;
  else canUpdate = !readOnly && !showVulnDetail && canDelete && hasVulns;

  const getWarningMessage = () => {
    if (isExploreAssetsTab) return intl.formatMessage({ id: 'modalWarning.selectOneOrMore' }, { entity: 'assets' });
    if (isExploreServicesTab) return intl.formatMessage({ id: 'modalWarning.selectOneOrMore' }, { entity: 'services' });
    return vulnsWarning;
  };

  const handleClick = () => {
    if (!canUpdate) return;

    if (isExploreTab) {
      if (exploreSelected.length > 0) setShowExploreConfirm(true);
      else setShowWarning(true);
      return;
    }

    if (vulnsSelected.length > 0) showModalDelete();
    else setShowWarning(true);
  };

  const handleExploreConfirm = () => {
    setShowExploreConfirm(false);
    if (isExploreAssetsTab) deleteExploreHosts();
    else deleteExploreServices();
  };

  const isDisabled = isExploreTab ? !canUpdate : !canUpdate;

  return (
    <>
      <Button variant="ghost" startIcon={ <Trash /> } title="Delete" ariaLabel="Delete" onClick={ handleClick } disabled={ isDisabled } />
      { showWarning && <ModalWarning onCloseCallback={ () => setShowWarning(false) } title="Delete" description={ getWarningMessage() } /> }
      { showExploreConfirm && (
        <ExploreDeleteConfirmModal
          count={ exploreSelected.length }
          entity={ exploreEntity }
          onConfirm={ handleExploreConfirm }
          onCancel={ () => setShowExploreConfirm(false) }
        />
      ) }
    </>
  );
};

export default DeleteButton;
